import { appApi } from '../app_api';

export const roundsApi = appApi.injectEndpoints({
  endpoints: (builder) => ({
    addRound: builder.mutation({
      query: ({ eventId, round }) => ({
        url: `/events/${eventId}/rounds`,
        method: 'POST',
        body: { round },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'EventCurrentRound', id: eventId }],
    }),
    getRounds: builder.query({
      query: (eventId) => `/events/${eventId}/rounds`,
      transformResponse: (response) => Array.isArray(response.rounds) ? response.rounds : [],
      providesTags: (result, error, eventId) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Round', id })),
              { type: 'EventRounds', id: eventId },
            ]
          : [{ type: 'EventRounds', id: eventId }],
    }),
    getRound: builder.query({
      query: ({ eventId, roundId }) => `/events/${eventId}/rounds/${roundId}`,
      transformResponse: (response) => response.round,
      providesTags: (result, error, { roundId }) => [{ type: 'Round', id: roundId }],
    }),
    getRobinRounds: builder.query({
      query: (eventId) => `/events/${eventId}/rounds?type=robin`,
      transformResponse: (response) => Array.isArray(response.rounds) ? response.rounds : [],
      providesTags: (result, error, eventId) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Round', id })),
              { type: 'EventRobinRounds', id: eventId },
            ]
          : [{ type: 'EventRobinRounds', id: eventId }],
    }),
    getBracketRounds: builder.query({
      query: (eventId) => `/events/${eventId}/rounds?type=bracket`,
      transformResponse: (response) => Array.isArray(response.rounds) ? response.rounds : [],
      providesTags: (result, error, eventId) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Round', id })),
              { type: 'EventBracketRounds', id: eventId },
            ]
          : [{ type: 'EventBracketRounds', id: eventId }],
    }),
    getBrackets: builder.query({
      query: ({ roundId, confirming }) => `/rounds/${roundId}/brackets?confirming=${confirming}`,
      transformResponse: (response) => Array.isArray(response.data) ? response.data : [],
      providesTags: (result, error, { roundId }) => 
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Bracket', id })),
              { type: 'RoundBrackets', id: roundId },
            ]
          : [{ type: 'RoundBrackets', id: roundId }],
    }),
    getCurrentRound: builder.query({
      query: (eventId) => `/events/${eventId}/rounds/current`,
      transformResponse: (response) => response.round || null,
      providesTags: (result, error, eventId) => 
        result
          ? [{ type: 'Round', id: result.id }, { type: 'EventCurrentRound', id: eventId }]
          : [{ type: 'EventCurrentRound', id: eventId }],
    }),
    getFinishedAllMatchesRound: builder.query({
      query: ({ roundId }) =>
        `/rounds/${roundId}/finished_all_matches`,
      transformResponse: (response) => response.data, // response is true/false
      providesTags: (result, error, { roundId }) => [
        { type: 'EventFinishedAllMatchesRound', id: roundId },
      ],
    }),
    getGroupsReviewedRound: builder.query({
      query: ({ roundId }) =>
        `/rounds/${roundId}/groups_reviewed`,
      transformResponse: (response) => response.data, // response is true/false
      providesTags: (result, error, { roundId }) => [
        { type: 'EventGroupsReviewedRound', id: roundId },
      ],
    }),
    getQualifiedPlayersCount: builder.query({
      query: ({ roundId }) =>
        `/rounds/${roundId}/qualified_players_count`,
      transformResponse: (response) => response.data,
      providesTags: (result, error, { roundId }) => [
        { type: 'RoundQualifiedPlayersCount', id: roundId },
      ],
    }),
    getCanContinue: builder.query({
      query: ({ roundId }) =>
        `/rounds/${roundId}/can_continue`,
      transformResponse: (response) => response.data, // response is true/false
      providesTags: (result, error, { roundId }) => [
        { type: 'RoundCanContinue', id: roundId },
      ],
    }),
    confirmGroups: builder.mutation({
      query: ({ roundId, players }) => ({
        url: `/rounds/${roundId}/confirm_groups`,
        method: 'PUT',
        body:{
          playersData: players,
        },
      }),
      invalidatesTags: (result, error, { eventId, roundId }) => {
        const groupIds = result?.modifiedGroups || [];
        return [
          { type: 'RoundGroups', id: roundId },
          { type: 'Round', id: roundId },
          { type: 'EventRounds', id: eventId },
          { type: 'Event', id: eventId },
          ...groupIds.map(id => ({ type: 'Group', id })),
        ];
      },
    }),
    confirmBrackets: builder.mutation({
      query: ({ roundId, playersData }) => ({
        url: `/rounds/${roundId}/confirm_brackets`,
        method: 'PUT',
        body:{
          playersData: playersData,
        },
      }),
      invalidatesTags: (result, error, { eventId, roundId }) => [
        { type: 'RoundBrackets', id: roundId },
        { type: 'Round', id: roundId },
        { type: 'EventRounds', id: eventId },
        { type: 'Event', id: eventId },
      ],
    }),
    deleteRound: builder.mutation({
      query: ({ roundId }) => ({
        url: `/rounds/${roundId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { eventId, roundId }) => [
        { type: 'EventRounds', id: eventId },
        { type: 'Round', id: roundId },
      ],
    }),
    nextRound: builder.mutation({
      query: (newRound) => ({
        url: `/events/${newRound.eventId}/rounds/next_round`,
        method: 'POST',
        body: newRound,
      }),
      invalidatesTags: (result, error, { eventId }) => 
        result.winner
        ?
        [
          { type: 'Event', id: eventId },
          { type: 'EventCurrentRound', id: eventId },
          { type: 'EventRounds', id: eventId },
          { type: 'EventRobinRounds', id: eventId },
          { type: 'EventBracketRounds', id: eventId },
        ]
        :
        [
          { type: 'EventCurrentRound', id: eventId },
          { type: 'EventRounds', id: eventId },
          { type: 'EventRobinRounds', id: eventId },
          { type: 'EventBracketRounds', id: eventId },
        ],
    }),
    // Mints (idempotently) the score-entry QR tokens for a round's group sheets.
    // Returns { [groupId]: token }.
    generateScoreEntryTokens: builder.mutation({
      query: ({ roundId }) => ({
        url: `/rounds/${roundId}/groups/score_entry_tokens`,
        method: 'POST',
      }),
      transformResponse: (response) => response.tokens || {},
      // Newly-minted codes should show up in the config-tab QR list.
      invalidatesTags: (result, error, { eventId }) =>
        eventId ? [{ type: 'EventScoreEntryTokens', id: eventId }] : [],
    }),
  }),
});

export const {
  useAddRoundMutation,
  useGenerateScoreEntryTokensMutation,
  useGetRoundsQuery,
  useGetRoundQuery,
  useGetRobinRoundsQuery,
  useGetBracketRoundsQuery,
  useGetCurrentRoundQuery,
  useGetFinishedAllMatchesRoundQuery,
  useGetGroupsReviewedRoundQuery,
  useGetQualifiedPlayersCountQuery,
  useGetBracketsQuery,
  useConfirmGroupsMutation,
  useNextRoundMutation,
  useGetCanContinueQuery,
  useDeleteRoundMutation,
  useConfirmBracketsMutation,
} = roundsApi;